/**
 * Itinerary detail - the review-and-negotiate surface for one draft trip.
 *
 * Days grouped into Week 1 / Week 2 sections (5 weekdays per week) with
 * week subtotals; each day is a card of ordinal-pinned stops with a
 * drive estimate and an "Open route in Google Maps" deep link (pure URL
 * construction - see shared/lib/maps.ts). Coordinators can remove stops,
 * re-optimize a day, and inject nearby unrouted inspections (priority
 * first) before assigning an inspector + start date, which commits every
 * stop through the workflow engine's `schedule` action server-side.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import {
  ArrowLeft,
  CalendarPlus,
  ExternalLink,
  Loader2,
  Map as MapIcon,
  MapPin,
  Plus,
  RefreshCw,
  Route,
  Trash2,
  Undo2,
  UserRound,
} from 'lucide-react'
import { toast } from 'sonner'
import { AppShell } from '@/admin/components/app-shell'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Separator } from '@/shared/components/ui/separator'
import { toastApiError } from '@/shared/lib/api-error'
import { formatDistanceMeters } from '@/shared/lib/formatting'
import { buildDirectionsUrls, type MapPoint } from '@/shared/lib/maps'
import {
  useAddItineraryStop,
  useCancelItinerary,
  useItinerary,
  useItinerarySuggestions,
  useReleaseItinerary,
  useRemoveItineraryStop,
  useReoptimizeItineraryDay,
  type ItineraryDay,
  type ItineraryStatus,
} from '@/admin/hooks/use-inspections'
import { AssignItineraryDialog } from './assign-itinerary-dialog'
import { TripRouteMap } from './trip-route-map'

const STATUS_TONE: Record<
  ItineraryStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'secondary',
  scheduled: 'default',
  cancelled: 'outline',
}

const WEEKDAYS_PER_WEEK = 5

function formatDrive(seconds: number | null | undefined): string {
  // Same trap as the render guard: `!seconds` treated a measured zero as
  // "unknown" and printed a dash. A day whose stops need no driving between
  // them is a real answer, and now that days carry their own figure it is
  // one the coordinator actually sees.
  if (seconds == null) return '-'
  if (seconds === 0) return '0 m'
  if (seconds < 0) return '-'
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  return h ? `${h} h ${m ? `${m} m` : ''}`.trim() : `${m} m`
}

function formatDayDate(iso: string | null, locale: string): string | null {
  if (!iso) return null
  const d = new Date(`${iso}T00:00:00`)
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * The trip's last visit date: start date plus (day_count - 1) further
 * weekdays - mirroring the server's weekend-skipping day mapping.
 */
function tripEndDate(startIso: string, dayCount: number): string {
  const d = new Date(`${startIso}T00:00:00`)
  let remaining = Math.max(0, dayCount - 1)
  while (remaining > 0) {
    d.setDate(d.getDate() + 1)
    if (d.getDay() !== 0 && d.getDay() !== 6) remaining -= 1
  }
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function ItineraryDetailPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { locale } = useFormatting()
  const { can } = usePermissions()
  // Same gate as the queue's staging surfaces; the schedule commit is
  // permission-checked server-side regardless.
  const canSchedule = can('inspections.create')

  const { data: itinerary, isLoading, isError } = useItinerary(uuid)
  const removeStop = useRemoveItineraryStop(uuid)
  const reoptimize = useReoptimizeItineraryDay(uuid)
  const cancelMutation = useCancelItinerary()
  const releaseMutation = useReleaseItinerary(uuid)

  const [assignOpen, setAssignOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [releaseOpen, setReleaseOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [addStopDay, setAddStopDay] = useState<number | null>(null)
  const [showRoute, setShowRoute] = useState(false)

  const isDraft = itinerary?.status === 'draft'
  const isScheduled = itinerary?.status === 'scheduled'

  // Group days into weeks of 5 weekdays with per-week subtotals.
  // day_index is 1-based (route-optimizer convention).
  const weeks = useMemo(() => {
    const days = itinerary?.days ?? []
    const grouped: { weekIndex: number; days: ItineraryDay[] }[] = []
    for (const day of days) {
      const weekIndex = Math.floor((day.day_index - 1) / WEEKDAYS_PER_WEEK)
      let bucket = grouped.find((w) => w.weekIndex === weekIndex)
      if (!bucket) {
        bucket = { weekIndex, days: [] }
        grouped.push(bucket)
      }
      bucket.days.push(day)
    }
    return grouped.sort((a, b) => a.weekIndex - b.weekIndex)
  }, [itinerary])

  return (
    <AppShell>
      <main className="page-container py-10">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-[var(--ink-3)]">
            <Loader2 size={16} className="animate-spin" />
            {t('inspections.itinerary.loading', 'Loading itinerary…')}
          </div>
        ) : isError || !itinerary ? (
          <div className="flex flex-col items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/inspections/queue')}
              className="flex h-auto items-center gap-1 self-start px-0 text-xs font-normal text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
            >
              <ArrowLeft size={13} />
              {t('inspections.itinerary.backToQueue', 'Scheduling Queue')}
            </Button>
            <p className="text-sm text-[var(--ink-3)]">
              {t(
                'inspections.itinerary.loadError',
                'This itinerary could not be loaded. It may have been cancelled, or you may not have access to it.',
              )}
            </p>
          </div>
        ) : (
          <>
            {/* ── Header ─────────────────────────────────────────── */}
            <header className="flex flex-col gap-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/inspections/queue')}
                className="flex h-auto items-center gap-1 self-start px-0 text-xs font-normal text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
              >
                <ArrowLeft size={13} />
                {t('inspections.itinerary.backToQueue', 'Scheduling Queue')}
              </Button>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="font-display text-[42px] leading-[1.1] text-[var(--ink)]">
                      {itinerary.name}
                    </h1>
                    <Badge variant={STATUS_TONE[itinerary.status] ?? 'outline'}>
                      {itinerary.status}
                    </Badge>
                  </div>
                  {/* Labeled facts strip - the at-a-glance trip summary. */}
                  <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                    {[
                      [
                        t('inspections.itinerary.factStops', 'Stops'),
                        String(itinerary.stop_count),
                      ],
                      [
                        t('inspections.itinerary.factDays', 'Days'),
                        t(
                          'inspections.itinerary.weekdayCount',
                          '{{count}} weekdays',
                          { count: itinerary.day_count },
                        ),
                      ],
                      [
                        t('inspections.itinerary.factDistance', 'Distance'),
                        itinerary.total_distance_meters
                          ? formatDistanceMeters(
                              itinerary.total_distance_meters,
                              locale,
                            )
                          : '-',
                      ],
                      [
                        t('inspections.itinerary.factDrive', 'Drive (est.)'),
                        formatDrive(itinerary.total_duration_seconds),
                      ],
                      [
                        t('inspections.itinerary.factPace', 'Pace'),
                        t(
                          'inspections.itinerary.paceValue',
                          '{{minutes}} m/stop · {{hours}} h days',
                          {
                            minutes: itinerary.per_stop_minutes,
                            hours: Math.round(itinerary.max_day_minutes / 60),
                          },
                        ),
                      ],
                      ...(itinerary.origin_address
                        ? [
                            [
                              t(
                                'inspections.itinerary.factStartPoint',
                                'Start point',
                              ),
                              itinerary.origin_address,
                            ],
                          ]
                        : []),
                      ...(itinerary.end_address
                        ? [
                            [
                              t(
                                'inspections.itinerary.factEndPoint',
                                'End point',
                              ),
                              itinerary.end_address,
                            ],
                          ]
                        : []),
                      ...(itinerary.day_start_time
                        ? [
                            [
                              t(
                                'inspections.itinerary.factDayStart',
                                'Day start',
                              ),
                              itinerary.day_start_time,
                            ],
                          ]
                        : []),
                      [
                        t('inspections.itinerary.factTripStart', 'Trip start'),
                        itinerary.start_date
                          ? formatDayDate(itinerary.start_date, locale) ?? '-'
                          : t(
                              'inspections.itinerary.notScheduledYet',
                              'not scheduled yet',
                            ),
                      ],
                      [
                        t('inspections.itinerary.factTripEnd', 'Trip end'),
                        itinerary.start_date
                          ? formatDayDate(
                              tripEndDate(
                                itinerary.start_date,
                                itinerary.day_count,
                              ),
                              locale,
                            ) ?? '-'
                          : '-',
                      ],
                    ].map(([label, value]) => (
                      <div key={label} className="flex flex-col">
                        <dt className="text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                          {label}
                        </dt>
                        <dd className="text-sm text-[var(--ink)]">{value}</dd>
                      </div>
                    ))}
                    {itinerary.status === 'scheduled' && (
                      <div className="flex flex-col">
                        <dt className="text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                          {t('inspections.itinerary.factFieldTech', 'Field tech')}
                        </dt>
                        <dd className="flex items-center gap-1 text-sm text-[var(--ink)]">
                          <UserRound size={13} />
                          {itinerary.inspector_name ||
                            t('inspections.itinerary.unassigned', 'Unassigned')}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
                {isDraft && canSchedule && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setDiscardOpen(true)}
                      className="min-h-[44px] text-[var(--ink-2)]"
                    >
                      <Trash2 size={14} className="mr-1.5" />
                      {t('inspections.itinerary.discard', 'Discard')}
                    </Button>
                    <Button
                      onClick={() => setAssignOpen(true)}
                      className="min-h-[44px] bg-[var(--green,#1c5a48)] text-white hover:bg-[var(--green,#1c5a48)]/90"
                    >
                      <CalendarPlus size={14} className="mr-1.5" />
                      {t('inspections.itinerary.assignSchedule', 'Assign + schedule')}
                    </Button>
                  </div>
                )}
                {/* A scheduled trip is no longer a dead end: move it to
                    another tech or another week, or hand it back to the pool
                    as an editable draft. */}
                {isScheduled && canSchedule && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setReleaseOpen(true)}
                      className="min-h-[44px] text-[var(--ink-2)]"
                      data-testid="itinerary-release"
                    >
                      <Undo2 size={14} className="mr-1.5" />
                      Return to pool
                    </Button>
                    <Button
                      onClick={() => setReassignOpen(true)}
                      className="min-h-[44px] bg-[var(--green,#1c5a48)] text-white hover:bg-[var(--green,#1c5a48)]/90"
                      data-testid="itinerary-reassign"
                    >
                      <CalendarPlus size={14} className="mr-1.5" />
                      Move trip
                    </Button>
                  </div>
                )}
              </div>
            </header>

            {weeks.length > 0 && <TripShapeStrip days={itinerary.days} />}

            {weeks.length > 0 && (
              <WholeTripRoute
                days={itinerary.days}
                origin={
                  itinerary.origin_point
                    ? {
                        address: itinerary.origin_point.formatted_address,
                        lat: itinerary.origin_point.lat,
                        lng: itinerary.origin_point.lng,
                      }
                    : itinerary.origin_address
                      ? { address: itinerary.origin_address }
                      : undefined
                }
                end={
                  itinerary.end_point
                    ? {
                        address: itinerary.end_point.formatted_address,
                        lat: itinerary.end_point.lat,
                        lng: itinerary.end_point.lng,
                      }
                    : itinerary.end_address
                      ? { address: itinerary.end_address }
                      : undefined
                }
                routeShown={showRoute}
                onToggleRoute={() => setShowRoute((v) => !v)}
              />
            )}

            {weeks.length > 0 && showRoute && (
              <div className="mt-3">
                <TripRouteMap days={itinerary.days} />
              </div>
            )}

            {/* ── Weeks ──────────────────────────────────────────── */}
            {weeks.length === 0 ? (
              <div className="mt-8">
                <EmptyState
                  icon={Route}
                  title={t(
                    'inspections.itinerary.emptyTitle',
                    'No stops on this itinerary',
                  )}
                  description={t(
                    'inspections.itinerary.emptyDescription',
                    'Every stop was removed. Discard the draft or add stops from the suggestions.',
                  )}
                />
              </div>
            ) : (
              <div className="mt-8 flex flex-col gap-10">
                {weeks.map((week) => {
                  const weekStops = week.days.reduce(
                    (sum, d) => sum + d.stops.length,
                    0,
                  )
                  const weekDrive = week.days.reduce(
                    (sum, d) => sum + (d.drive_seconds_estimate ?? 0),
                    0,
                  )
                  return (
                    <section key={week.weekIndex}>
                      <div className="flex items-baseline justify-between">
                        <h2 className="font-display text-[26px] italic text-[var(--ink)]">
                          {t('inspections.itinerary.weekLabel', 'Week {{index}}', {
                            index: week.weekIndex + 1,
                          })}
                        </h2>
                        <span className="text-xs text-[var(--ink-3)]">
                          {t('inspections.itinerary.stopCount', '{{count}} stops', {
                            count: weekStops,
                          })}
                          {weekDrive
                            ? ` · ${t(
                                'inspections.itinerary.driveEst',
                                '{{duration}} drive (est.)',
                                { duration: formatDrive(weekDrive) },
                              )}`
                            : ''}
                        </span>
                      </div>
                      <Separator className="mt-2" />
                      <div className="mt-4 flex flex-col gap-5">
                        {week.days.map((day) => (
                          <DayCard
                            key={day.day_index}
                            day={day}
                            locale={locale}
                            origin={
                              itinerary.origin_point
                                ? {
                                    address:
                                      itinerary.origin_point.formatted_address,
                                    lat: itinerary.origin_point.lat,
                                    lng: itinerary.origin_point.lng,
                                  }
                                : itinerary.origin_address
                                  ? { address: itinerary.origin_address }
                                  : undefined
                            }
                            end={
                              itinerary.end_point
                                ? {
                                    address:
                                      itinerary.end_point.formatted_address,
                                    lat: itinerary.end_point.lat,
                                    lng: itinerary.end_point.lng,
                                  }
                                : itinerary.end_address
                                  ? { address: itinerary.end_address }
                                  : undefined
                            }
                            editable={!!isDraft && canSchedule}
                            onRemoveStop={async (inspectionUuid) => {
                              try {
                                await removeStop.mutateAsync(inspectionUuid)
                                toast.success(
                                  t(
                                    'inspections.itinerary.toastStopRemoved',
                                    'Stop removed - returned to the pool.',
                                  ),
                                )
                              } catch (e) {
                                toastApiError(
                                  e,
                                  t(
                                    'inspections.itinerary.toastStopRemoveError',
                                    'Could not remove this stop.',
                                  ),
                                )
                              }
                            }}
                            onReoptimize={async () => {
                              try {
                                await reoptimize.mutateAsync(day.day_index)
                                toast.success(
                                  t(
                                    'inspections.itinerary.toastDayReoptimized',
                                    'Day re-optimized.',
                                  ),
                                )
                              } catch (e) {
                                toastApiError(
                                  e,
                                  t(
                                    'inspections.itinerary.toastReoptimizeError',
                                    'Could not re-optimize this day.',
                                  ),
                                )
                              }
                            }}
                            reoptimizing={reoptimize.isPending}
                            onAddStop={() => setAddStopDay(day.day_index)}
                          />
                        ))}
                      </div>
                    </section>
                  )
                })}
              </div>
            )}

            {/* ── Dialogs ────────────────────────────────────────── */}
            {assignOpen && (
              <AssignItineraryDialog
                open={assignOpen}
                onOpenChange={setAssignOpen}
                itinerary={itinerary}
              />
            )}
            {/* Same dialog, pre-filled with what the trip already has - a
                move is an edit of the assignment, not a different concept,
                so it should not look like a different form. */}
            <AssignItineraryDialog
              mode="reassign"
              open={reassignOpen}
              onOpenChange={setReassignOpen}
              itinerary={itinerary}
            />
            <Dialog open={releaseOpen} onOpenChange={setReleaseOpen}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Return {itinerary.name} to the pool?</DialogTitle>
                  <DialogDescription>
                    The trip becomes an editable draft again with its stops
                    intact, and every booking on it is given up. Stops the
                    homeowner has already confirmed stay booked - they are
                    listed back to you rather than cancelled.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setReleaseOpen(false)}
                  >
                    Keep it booked
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={releaseMutation.isPending}
                    data-testid="itinerary-release-confirm"
                    onClick={async () => {
                      try {
                        const res = await releaseMutation.mutateAsync({})
                        const bits = [
                          `Returned ${res.released.length} stop${
                            res.released.length === 1 ? '' : 's'
                          } to the pool.`,
                        ]
                        if (res.skipped.length) {
                          bits.push(
                            `${res.skipped.length} left booked (already confirmed or under way).`,
                          )
                        }
                        toast.success(bits.join(' '))
                        setReleaseOpen(false)
                      } catch (e) {
                        toastApiError(e, 'Could not return this trip.')
                      }
                    }}
                  >
                    {releaseMutation.isPending
                      ? 'Returning…'
                      : 'Return to pool'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>
                    {t('inspections.itinerary.discardTitle', 'Discard {{name}}?', {
                      name: itinerary.name,
                    })}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      'inspections.itinerary.discardDescription',
                      'Every stop returns to the staged pool. Nothing is deleted - the next Smart Schedule run can pick them up again.',
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDiscardOpen(false)}>
                    {t('inspections.itinerary.keepDraft', 'Keep draft')}
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={cancelMutation.isPending}
                    onClick={async () => {
                      try {
                        await cancelMutation.mutateAsync(itinerary.uuid)
                        toast.success(
                          t(
                            'inspections.itinerary.toastDraftDiscarded',
                            'Draft discarded.',
                          ),
                        )
                        navigate('/inspections/queue')
                      } catch (e) {
                        toastApiError(
                          e,
                          t(
                            'inspections.itinerary.toastDiscardError',
                            'Could not discard this draft.',
                          ),
                        )
                      }
                    }}
                  >
                    {t('inspections.itinerary.discard', 'Discard')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            {addStopDay != null && uuid && (
              <AddStopDialog
                itineraryUuid={uuid}
                dayIndex={addStopDay}
                onClose={() => setAddStopDay(null)}
              />
            )}
          </>
        )}
      </main>
    </AppShell>
  )
}

/* ── Trip shape ───────────────────────────────────────────────────── */

/**
 * One strip, one segment per stop, coloured by contractor and divided by
 * day. It answers the questions a coordinator asks before reading any day
 * card: how many contractors is this trip, how are they spread, and does
 * anyone run over into a second day.
 *
 * That last part is the reason it earns its place rather than decorating
 * the page - a day the backend flagged `spans_group` is underlined, so the
 * "this contractor continues tomorrow" case is visible at a glance instead
 * of being inferred from repeated names down the cards.
 *
 * Everything here comes from the existing detail payload; no extra request.
 */
// Fixed class list rather than inline styles: the palette is a known,
// finite set, so it belongs in classes (see CLAUDE.md - inline style props
// are reserved for values that can only be computed at runtime).
const SHAPE_COLOR_CLASSES = [
  'bg-[var(--chart-1,#1c5a48)]',
  'bg-[var(--chart-2,#b07d3f)]',
  'bg-[var(--chart-3,#4a6fa5)]',
  'bg-[var(--chart-4,#7d5a8c)]',
  'bg-[var(--chart-5,#a34e4e)]',
  'bg-[var(--chart-6,#3f7d6e)]',
]

/**
 * The whole trip as one route, day 1 through day N.
 *
 * Each day card already links its own leg, which answers "where am I driving
 * on Thursday" but never "what does this trip actually look like" - and that
 * is the question a coordinator asks first, before committing a field tech to
 * a week away. Reading it off six separate links is not the same thing.
 *
 * Google caps waypoints per link, so `buildDirectionsUrls` splits long trips
 * into consecutive legs that overlap by one point. When that happens we say
 * so and number them, rather than quietly showing part of the trip as though
 * it were all of it.
 */
function WholeTripRoute({
  days,
  origin,
  end,
  routeShown,
  onToggleRoute,
}: {
  days: ItineraryDay[]
  origin?: { address?: string; lat?: number | null; lng?: number | null }
  end?: { address?: string; lat?: number | null; lng?: number | null }
  routeShown: boolean
  onToggleRoute: () => void
}) {
  const { stopCount, urls } = useMemo(() => {
    const ordered = [...days]
      .sort((a, b) => a.day_index - b.day_index)
      .flatMap((d) => d.stops)
    return {
      stopCount: ordered.length,
      urls: buildDirectionsUrls(
        ordered.map((s) => ({ address: s.address, lat: s.lat, lng: s.lng })),
        origin,
        end,
      ),
    }
  }, [days, origin, end])

  if (!urls.length) return null

  return (
    <section
      className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3"
      aria-label="Whole trip route"
      data-testid="whole-trip-route"
    >
      <span className="text-[13px] text-[var(--ink-2)]">
        Whole trip · {days.length} day{days.length === 1 ? '' : 's'} ·{' '}
        {stopCount} stop{stopCount === 1 ? '' : 's'}
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-1.5">
        <Button size="xs" variant="outline" onClick={onToggleRoute}>
          <MapIcon size={12} className="mr-1" />
          {routeShown ? 'Hide route' : 'Display route'}
        </Button>
        {urls.length > 1 && (
          <span className="text-[11px] text-[var(--ink-3)]">
            Split into {urls.length} legs - Google caps stops per link
          </span>
        )}
        {urls.map((url, i) => (
          <Button
            key={url}
            size="xs"
            variant="outline"
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink size={12} className="mr-1" />
            {urls.length > 1
              ? `Leg ${i + 1}/${urls.length}`
              : 'Open whole trip in Google Maps'}
          </Button>
        ))}
      </div>
    </section>
  )
}

function TripShapeStrip({ days }: { days: ItineraryDay[] }) {
  const { t } = useTranslation()
  const unassignedLabel = t('inspections.itinerary.unassigned', 'Unassigned')
  const { contractors, colorOf } = useMemo(() => {
    const names: string[] = []
    for (const day of days) {
      for (const stop of day.stops) {
        const name = stop.contractor_name || unassignedLabel
        if (!names.includes(name)) names.push(name)
      }
    }
    return {
      contractors: names,
      colorOf: (name: string) =>
        SHAPE_COLOR_CLASSES[
          names.indexOf(name) % SHAPE_COLOR_CLASSES.length
        ],
    }
  }, [days, unassignedLabel])

  const totalStops = days.reduce((sum, d) => sum + d.stops.length, 0)
  if (totalStops === 0) return null

  return (
    <section
      className="mt-6"
      aria-label={t('inspections.itinerary.tripShapeAria', 'Trip shape')}
      data-testid="trip-shape"
    >
      <div className="flex items-end gap-1">
        {days.map((day) => (
          <div
            key={day.day_index}
            className="flex flex-col gap-1"
            style={{ flexGrow: day.stops.length, flexBasis: 0 }}
          >
            <div className="flex h-2.5 gap-px overflow-hidden rounded-[2px]">
              {day.stops.map((stop) => (
                <div
                  key={stop.inspection_uuid}
                  className={`flex-1 ${colorOf(
                    stop.contractor_name || unassignedLabel,
                  )}`}
                  title={`${stop.contractor_name || unassignedLabel} - ${stop.code}`}
                />
              ))}
            </div>
            <span
              className={
                day.spans_group
                  ? 'border-b-2 border-b-[var(--ink-3)] pb-0.5 text-center text-[10px] text-[var(--ink-2)]'
                  : 'text-center text-[10px] text-[var(--ink-3)]'
              }
              title={
                day.spans_group
                  ? t(
                      'inspections.itinerary.spansDayBoundaryTooltip',
                      'A contractor visit continues across this day boundary.',
                    )
                  : undefined
              }
            >
              {t('inspections.itinerary.dayLabel', 'Day {{index}}', {
                index: day.day_index,
              })}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {contractors.map((name) => (
          <span
            key={name}
            className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)]"
          >
            <span className={`h-2 w-2 rounded-[1px] ${colorOf(name)}`} />
            {name}
          </span>
        ))}
      </div>
    </section>
  )
}

/* ── Day card ─────────────────────────────────────────────────────── */

function DayCard({
  day,
  locale,
  origin,
  end,
  editable,
  onRemoveStop,
  onReoptimize,
  reoptimizing,
  onAddStop,
}: {
  day: ItineraryDay
  locale: string
  origin?: MapPoint
  end?: MapPoint
  editable: boolean
  onRemoveStop: (inspectionUuid: string) => void
  onReoptimize: () => void
  reoptimizing: boolean
  onAddStop: () => void
}) {
  const { t } = useTranslation()
  const dateLabel = formatDayDate(day.date, locale)
  const mapsUrls = useMemo(
    () =>
      buildDirectionsUrls(
        day.stops.map((s) => ({ address: s.address, lat: s.lat, lng: s.lng })),
        origin,
        end,
      ),
    [day.stops, origin, end],
  )

  return (
    <div
      className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)]"
      data-testid="day-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b-[0.5px] border-b-[var(--rule)] px-4 py-3">
        <div className="flex items-baseline gap-2.5">
          <span className="text-sm font-medium text-[var(--ink)]">
            {t('inspections.itinerary.dayLabel', 'Day {{index}}', {
              index: day.day_index,
            })}
          </span>
          {dateLabel && (
            <span className="text-xs text-[var(--ink-2)]">{dateLabel}</span>
          )}
          {day.spans_group && (
            <Badge
              variant="outline"
              className="px-1.5 py-0 text-[10px] font-normal"
              title={t(
                'inspections.itinerary.continuesTooltip',
                'One contractor has more work here than fits in a day, so their visit continues across days.',
              )}
            >
              {t('inspections.itinerary.continuesBadge', 'Continues')}
            </Badge>
          )}
          <span className="text-xs text-[var(--ink-3)]">
            {t('inspections.itinerary.stopCount', '{{count}} stops', {
              count: day.stops.length,
            })}
            {/* `!= null`, not truthiness: a day with genuinely no driving
                is 0, and 0 is falsy, so the old guard hid a real answer
                behind the same blank as "we don't know". */}
            {day.drive_seconds_estimate != null
              ? ` · ${t('inspections.itinerary.driveLabel', '{{duration}} drive', {
                  duration: formatDrive(day.drive_seconds_estimate),
                })}`
              : ''}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {editable && (
            <>
              <Button size="xs" variant="ghost" onClick={onAddStop}>
                <Plus size={12} className="mr-1" />
                {t('inspections.itinerary.addStop', 'Add stop')}
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={onReoptimize}
                disabled={reoptimizing || day.stops.length < 2}
              >
                <RefreshCw
                  size={12}
                  className={`mr-1 ${reoptimizing ? 'animate-spin' : ''}`}
                />
                {t('inspections.itinerary.reoptimize', 'Re-optimize')}
              </Button>
            </>
          )}
          {mapsUrls.map((url, i) => (
            <Button
              key={url}
              size="xs"
              variant="outline"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink size={12} className="mr-1" />
              {mapsUrls.length > 1
                ? t('inspections.itinerary.openRouteMulti', 'Open route {{index}}/{{total}}', {
                    index: i + 1,
                    total: mapsUrls.length,
                  })
                : t('inspections.itinerary.openRouteSingle', 'Open route in Google Maps')}
            </Button>
          ))}
        </div>
      </div>
      <ol className="flex flex-col">
        {day.stops.map((stop, i) => (
          <li
            key={stop.inspection_uuid}
            className="flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] px-4 py-2.5 last:border-b-0"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--green,#1c5a48)] text-[11px] font-medium text-white">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-[var(--ink)]">
                {stop.address || t('inspections.itinerary.noAddress', 'No address')}
              </p>
              <p className="truncate text-[11px] text-[var(--ink-3)]">
                <Link
                  to={`/inspections/${stop.inspection_uuid}`}
                  className="font-mono text-[var(--ink-2)] hover:underline"
                  title={t(
                    'inspections.itinerary.openInspectionTooltip',
                    'Open the inspection record (registration details, pitch, storeys, safety notes)',
                  )}
                >
                  {stop.code}
                </Link>
                {stop.contractor_name ? ` · ${stop.contractor_name}` : ''}
                {stop.sub_type_label ? ` · ${stop.sub_type_label}` : ''}
              </p>
            </div>
            {stop.trigger_source !== 'from_registration' && (
              <Badge variant="outline" className="shrink-0">
                {t('inspections.itinerary.priority', 'priority')}
              </Badge>
            )}
            {editable && (
              <Button
                size="icon-xs"
                variant="ghost"
                className="shrink-0 text-[var(--ink-3)] hover:text-[var(--status-error,#991b1b)]"
                onClick={() => onRemoveStop(stop.inspection_uuid)}
                aria-label={t('inspections.itinerary.removeStop', 'Remove stop')}
              >
                <Trash2 size={13} />
              </Button>
            )}
          </li>
        ))}
      </ol>
    </div>
  )
}

/* ── Add-stop dialog (nearby suggestions, priority first) ─────────── */

function AddStopDialog({
  itineraryUuid,
  dayIndex,
  onClose,
}: {
  itineraryUuid: string
  dayIndex: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { locale } = useFormatting()
  const [search, setSearch] = useState('')
  const { data: suggestions, isLoading, isFetching } = useItinerarySuggestions(
    itineraryUuid,
    dayIndex,
    true,
    search,
  )
  const addStop = useAddItineraryStop(itineraryUuid)

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('inspections.itinerary.addStopTitle', 'Add a stop to Day {{day}}', {
              day: dayIndex,
            })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'inspections.itinerary.addStopDescription',
              'Staged inspections not yet on any trip, same state first - priority (claim-triggered) leading, then high-priority contractors, then oldest. Out-of-state options are labeled.',
            )}
          </DialogDescription>
        </DialogHeader>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t(
            'inspections.itinerary.searchPlaceholder',
            'Search by code, address, city or contractor…',
          )}
        />
        {/* The way back out of a search, available the whole time a search is
            narrowing - not only when it happens to return nothing. Filtering
            12 down to 3 hides nine options just as effectively as filtering
            to 0, and until now that case offered no route back at all beyond
            a small clear icon. The count is what makes "everything" mean
            something rather than being a leap of faith. */}
        {search.trim() && (
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-[11px] text-[var(--ink-3)]">
              {t('inspections.itinerary.filteredBy', 'Filtered by "{{query}}"', {
                query: search.trim(),
              })}
            </span>
            <Button
              size="xs"
              variant="ghost"
              className="px-1.5 text-[var(--ink-2)]"
              onClick={() => setSearch('')}
            >
              {t(
                'inspections.itinerary.showEverything',
                'Show everything available',
              )}
            </Button>
          </div>
        )}
        {isLoading || (isFetching && !suggestions?.length) ? (
          <div className="flex items-center gap-2 py-4 text-sm text-[var(--ink-3)]">
            <Loader2 size={14} className="animate-spin" />
            {t('inspections.itinerary.findingStops', 'Finding stops…')}
          </div>
        ) : !suggestions?.length ? (
          // An empty list here is not a dead end, and saying only "nothing
          // found" leaves the user with no idea whether they mistyped, or
          // whether there is genuinely nothing to add. Name the cause and
          // the way out.
          <div className="flex flex-col gap-2 py-4">
            <p className="text-sm text-[var(--ink-2)]">
              {search.trim()
                ? t(
                    'inspections.itinerary.noMatch',
                    'No unrouted inspection matches "{{query}}".',
                    { query: search.trim() },
                  )
                : t(
                    'inspections.itinerary.nothingUnroutedLeft',
                    'Every staged inspection is already on a trip, so there is nothing unrouted left to add.',
                  )}
            </p>
            <p className="text-[12px] text-[var(--ink-3)]">
              {search.trim()
                ? t(
                    'inspections.itinerary.clearSearchHint',
                    'Clear the search to see everything that is available - the list is the point here, searching is only for narrowing it.',
                  )
                : t(
                    'inspections.itinerary.freeUpHint',
                    'To free some up: cancel a draft trip you do not want, or stage more homes from the Pool. Removing a stop from another day of this trip does not release it - it stays on the trip.',
                  )}
            </p>
          </div>
        ) : (
          <div className="flex max-h-[320px] flex-col gap-1.5 overflow-y-auto py-1">
            {!search.trim() && (
              <p className="px-1 pb-1 text-[11px] text-[var(--ink-3)]">
                {t(
                  'inspections.itinerary.countAvailable',
                  '{{count}} available - pick one, or search to narrow the list.',
                  { count: suggestions.length },
                )}
              </p>
            )}
            {[...suggestions]
              .sort(
                (a, b) =>
                  (a.distance_km ?? Number.POSITIVE_INFINITY) -
                  (b.distance_km ?? Number.POSITIVE_INFINITY),
              )
              .map((s) => (
              <div
                key={s.inspection_uuid}
                className="flex items-center justify-between gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-sm text-[var(--ink)]">
                    <MapPin size={12} className="shrink-0 text-[var(--ink-3)]" />
                    {s.address || s.code}
                  </p>
                  <p className="truncate text-[11px] text-[var(--ink-3)]">
                    {s.code}
                    {s.contractor_name ? ` · ${s.contractor_name}` : ''}
                    {s.sub_type_label ? ` · ${s.sub_type_label}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {/* The question behind adding a stop is "is it near the
                      others" - so answer it, and name the stop rather than
                      just quoting a number. */}
                  {s.distance_km != null && (
                    <Badge
                      variant={s.distance_km <= 25 ? 'default' : 'outline'}
                      title={
                        s.nearest_stop_index != null
                          ? t(
                              'inspections.itinerary.closestToStop',
                              'Closest to stop {{index}} on this day (straight line)',
                              { index: s.nearest_stop_index },
                            )
                          : undefined
                      }
                    >
                      {formatDistanceMeters(s.distance_km * 1000, locale)}
                      {s.nearest_stop_index != null
                        ? t('inspections.itinerary.fromStop', ' from stop {{index}}', {
                            index: s.nearest_stop_index,
                          })
                        : ''}
                    </Badge>
                  )}
                  {!s.in_trip_state && s.property_state && (
                    <Badge variant="secondary">{s.property_state}</Badge>
                  )}
                  {s.trigger_source !== 'from_registration' && (
                    <Badge variant="outline">
                      {t('inspections.itinerary.priority', 'priority')}
                    </Badge>
                  )}
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={addStop.isPending}
                    onClick={async () => {
                      try {
                        await addStop.mutateAsync({
                          inspection_uuid: s.inspection_uuid,
                          day_index: dayIndex,
                        })
                        toast.success(
                          t(
                            'inspections.itinerary.toastStopAdded',
                            'Stop added to the end of the day.',
                          ),
                        )
                        onClose()
                      } catch (e) {
                        toastApiError(
                          e,
                          t('inspections.itinerary.toastStopAddError', 'Could not add this stop.'),
                        )
                      }
                    }}
                  >
                    <Plus size={12} className="mr-1" />
                    {t('inspections.itinerary.add', 'Add')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
